import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { api, formatApiErrorDetail, formatDateTimeBR } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  COPY_STEPUP,
  STEP_UP_ACTION_TOKEN_CREATE,
  STEP_UP_ENDPOINT,
  STEP_UP_HEADER,
  isStepUpRequired,
  isTwoFactorEnrollmentRequired,
  newIdempotencyKey,
  perfilDeepLink,
} from "../lib/stepUp";
import StepUpDialog from "../components/StepUpDialog";
import {
  CREDENTIAL_KINDS,
  CREDENTIAL_KIND_META,
  IP_ALLOWLIST_HINT_IP,
  IP_ALLOWLIST_MAX,
  LABEL_MAX,
  SCOPE_MODES_BY_KIND,
  SK_SCOPE_COUNT,
  TOKEN_SCOPE_GROUPS,
  TOKEN_SCOPE_OPTIONS,
  TOKEN_SCOPE_READ_PRODUCTS,
  applyIpAllowlistPaste,
  buildCreateTokenPayload,
  formatTokenCreateError,
  initialScopeState,
  ipAllowlistEntryMarks,
  ipAllowlistError,
  ipAllowlistListError,
  issuedCredentialKind,
  kindMismatch,
  labelError,
  nextKindState,
  nextScopeState,
  parseIpAllowlist,
  pendingRotationByOriginId,
  requiresIpAllowlist,
  rotationPrefill,
  toggleScopeState,
  tokenCapacityReached,
} from "../lib/tokenScopes";
import {
  capacityFraming,
  lastUsedSummary,
  revocationCandidates,
  shouldPromptCleanup,
  slotsRemaining,
} from "../lib/tokenHygiene";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Key, Plus, RefreshCw, Loader2, Trash2, Copy, Check, ShieldAlert, ShieldCheck,
  CloudOff, Eye, EyeOff, RotateCw, AlertTriangle, Info, CheckCircle2, XCircle, Circle
} from "lucide-react";

// Padrao de alerta da casa (NewOperation.jsx, Policy.jsx): caixa com borda +
// fundo tonal, nunca um <p> colorido solto. `warning` = precisa de acao/atencao;
// `info` = contexto. A cor CARREGA semantica — trocar uma pela outra mente.
const ALERT_TONE = {
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-blue-200 bg-blue-50 text-blue-900",
};

// Aviso inline no padrao da casa. `role="alert"` so quando o aviso NASCE de uma
// acao do master (senao o leitor de tela interrompe a leitura da tela inteira).
function InlineAlert({ tone = "warning", icon: Icon, role, id, testId, children }) {
  return (
    <div
      id={id}
      role={role}
      className={`flex items-start gap-2 rounded-md border p-3 text-[13px] ${ALERT_TONE[tone]}`}
      data-testid={testId}
    >
      {Icon && <Icon className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />}
      <span>{children}</span>
    </div>
  );
}

// Doc PUBLICA de autenticacao. Pagina e ancora JA publicadas hoje (main da V2);
// a secao SK/RK da F6 ainda nao existe no ar, entao NAO se linka para ela.
const AUTH_DOCS_URL =
  "https://docs.zemocapital.com/auth/overview#client-credentials-token-exchange";

// Motivo VISIVEL de "criar"/"rotacionar" bloqueados — mora no painel de faxina
// e e apontado por `aria-describedby` (M4).
const CAPACITY_REASON_ID = "token-capacity-reason";

// Numero de permissoes — SEMPRE derivado, nunca escrito na copy. O catalogo ja
// foi de 7 para 9 (F2.1) e de 9 para 12 (2026-07-29) e a copy envelheceu.
//
// DOIS numeros diferentes, de proposito: o catalogo de SELECAO (12, o que a
// tela oferece a uma RK) e o recorte da SK (9, o que uma chave de acesso
// completo carrega). Trocar um pelo outro na copy volta a mentir ao usuario.
const SCOPE_COUNT = TOKEN_SCOPE_OPTIONS.length;

// "A, B e C" — enumeracao PT-BR para a copy derivada do catalogo.
const listPtBr = (items) =>
  items.length <= 1
    ? items[0] ?? ""
    : `${items.slice(0, -1).join(", ")} e ${items[items.length - 1]}`;

const tokenKey = (token) => token?.token_id || token?.id;

// COPY do campo de allowlist de IP — texto FIXADO pelo owner (TK2-A C1004423;
// a tag `ipAllowlistOptional`, C1004421). Estado final: a recusa por origem JÁ
// vale (TK4-C C1004183, na V2) — com escopo de escrita a lista é obrigatória e
// chamada de IP fora dela é recusada; sem escrita a lista é opcional e, se
// preenchida, também recusa (vazia = sem restrição por IP). `simulation:create`
// não conta como escrita (WRITE_SCOPE_EXCEPTIONS em ../lib/tokenScopes).
//
// Ponto ÚNICO da copy do campo de allowlist (molde da TokenRequestsPage, leva
// FS-030): o rito /myuxreview + a ratificação do owner trocam texto AQUI, sem
// caçar string no JSX. As mensagens de ERRO (inválido / CIDR / obrigatório /
// teto / unspecified) moram em `COPY_IP` de ../lib/tokenScopes, porque nascem
// dentro das funções de validação — os dois são o mesmo item de ratificação.
//
// `hintIp` é PLACEHOLDER/dica, nunca valor pré-preenchido: pré-preencher
// autorizaria um IP que o master não escolheu.
const COPY = {
  ipAllowlistLegend: "3. IPs autorizados",
  ipAllowlistLabel: "IPs autorizados",
  ipAllowlistOptional: "opcional para credenciais sem escrita",
  ipAllowlistRequiredTag: "obrigatório",
  ipAllowlistHint:
    "Informe um IP por linha (IPv4 ou IPv6 individual — sem faixas nem máscara).",
  // Motivo do campo com escopo de ESCRITA (`ipRequired`). Irmão condicional de
  // `ipAllowlistReadOnlyWhy`: a tela mostra UM dos dois, nunca os dois.
  ipAllowlistRequiredWhy:
    "Esta credencial tem permissão de escrita, por isso a lista é obrigatória. " +
    "Chamadas feitas com ela a partir de um IP fora da lista são recusadas.",
  // Motivo do campo SEM escopo de escrita (`!ipRequired`; inclui a credencial só
  // com `simulation:create`, que não conta como escrita).
  ipAllowlistReadOnlyWhy:
    "Esta credencial não tem permissão de escrita, por isso a lista é opcional. " +
    "Se você preenchê-la, chamadas feitas a partir de um IP fora da lista são " +
    "recusadas; se deixá-la vazia, a credencial não tem restrição por IP.",
  ipAllowlistNatHint:
    `Se a integração sai pela infraestrutura da Zemo, o IP de saída de produção é ${IP_ALLOWLIST_HINT_IP}.`,
  ipAllowlistPlaceholder: `${IP_ALLOWLIST_HINT_IP}\n2001:db8::1`,
  ipAllowlistCount: (n) => `${n} de ${IP_ALLOWLIST_MAX} entradas`,
};

// Destino do deep-link de remediação: o master sai para o perfil, ativa o 2FA
// e VOLTA para cá (`Profile.jsx` lê o `?retorno=` e valida contra o allowlist
// de `lib/stepUp`).
const TOKENS_ROUTE = "/integracao/tokens";
const PERFIL_2FA_LINK = perfilDeepLink(TOKENS_ROUTE);

export default function TokensAPI() {
  const [tokens, setTokens] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [activeLimit, setActiveLimit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState("loading");
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  // Toda a selecao de escopos (e a classe SK/RK) vive na maquina pura de
  // ../lib/tokenScopes — o componente segue consumidor burro.
  const [scopeState, setScopeState] = useState(initialScopeState);
  // Allowlist de IP (FS-032). Texto CRU do campo — uma entrada por linha; o
  // parse/validação vivem em ../lib/tokenScopes, como o resto da regra.
  const [ipAllowlistText, setIpAllowlistText] = useState("");
  // UX-4 (achado /myuxreview): o erro "obrigatório" + aria-invalid não podem
  // GRITAR no campo pristino — só marcar um scope de escrita não é o usuário
  // errando. Espelha o padrão de `labelProblem` (que só acusa após digitar):
  // o erro aparece depois do primeiro toque (onChange/onBlur). O BLOQUEIO de
  // submit é independente disto — write sem IP nunca cria.
  const [ipTouched, setIpTouched] = useState(false);
  // Máscara A (validação ao vivo, sem bloquear tecla): posição do cursor no campo
  // (`null` = campo sem foco, ou acabou de receber uma colagem). A linha que
  // CONTÉM o cursor está sendo escrita e só ganha marca válida/inválida quando o
  // cursor SAI dela; ver `ipAllowlistEntryMarks` (../lib/tokenScopes).
  const [ipCaret, setIpCaret] = useState(null);
  const ipInputRef = useRef(null);
  // Cursor a restaurar depois que a colagem re-renderiza o campo, e o eco dele:
  // o navegador emite um `select` quando o cursor é posto por código; ele não é o
  // usuário saindo da linha e não pode desmarcar a última linha colada.
  const ipPendingCaretRef = useRef(null);
  const ipPasteCaretRef = useRef(null);
  // Uma por OPERAÇÃO de colagem normalizada: a restauração do cursor corre a cada colagem, também
  // quando o texto normalizado é IGUAL ao anterior (o `ipAllowlistText` não muda e, sem isto, a
  // seleção nativa — p.ex. a lista inteira — ficava de pé e a tecla seguinte a substituía).
  const [ipPasteSeq, setIpPasteSeq] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  // Rotacao: criar -> migrar -> revogar. Guarda a credencial de ORIGEM para
  // lembrar o master de revoga-la DEPOIS de migrar (nunca revoga sozinho).
  const [rotatingFrom, setRotatingFrom] = useState(null);
  const [droppedScopes, setDroppedScopes] = useState([]);

  // One-time secret display after creation
  const [createdToken, setCreatedToken] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [copyFailedField, setCopyFailedField] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  // O banner one-time e a unica chance de ver o secret: ele recebe foco
  // programatico ao aparecer (padrao da casa, NewOperation.jsx) para que leitor
  // de tela e teclado nao percam a informacao mais consequente da tela.
  const bannerRef = useRef(null);
  const [dismissArmed, setDismissArmed] = useState(false);

  // Revogacao: acao IRREVERSIVEL, sempre por AlertDialog nomeado — nunca pelo
  // window.confirm generico do browser. `revokeTarget` guarda a credencial
  // escolhida e a origem do gatilho (lista / faxina / rotacao).
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeTyped, setRevokeTyped] = useState("");
  const [revoking, setRevoking] = useState(false);

  // ── Step-up 2FA na emissão (FS-035) ────────────────────────────────────
  // `=== false` e não `!user?.two_factor_enabled`: enquanto o /auth/me não
  // volta, `user` é null e "não sei" NÃO é "não tem" — um banner piscando na
  // carga ensinaria o master a ignorá-lo. Só o dado explícito bloqueia.
  const auth = useAuth();
  const user = auth?.user;
  const semSegundoFator = user?.two_factor_enabled === false;

  const [blockOpen, setBlockOpen] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);
  const [stepUpError, setStepUpError] = useState(null);
  // Chave de idempotência da TENTATIVA: estável enquanto o formulário não
  // muda, para que o retry do estado 3 (prova expirada) não possa duplicar a
  // credencial num sucesso-fantasma. Ref e não state — muda sem re-render.
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setSyncStatus("loading");
    try {
      const { data } = await api.get("/v2/tokens");
      const list = Array.isArray(data) ? data : (data.tokens || data.items || []);
      setTokens(list);
      setActiveCount(
        Number.isInteger(data?.active_count)
          ? data.active_count
          : list.filter((token) => token.status === "active").length
      );
      setActiveLimit(Number.isInteger(data?.active_limit) ? data.active_limit : null);
      setSyncStatus("synced");
    } catch (e) {
      if (e?.response?.status === 403) {
        setSyncStatus("synced");
        setTokens([]);
        setActiveCount(0);
        setActiveLimit(null);
      } else {
        console.error("[TokensAPI] Failed to load tokens:", e?.response?.status, e?.message);
        setSyncStatus("offline");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const limitReached = tokenCapacityReached(activeCount, activeLimit);
  // Teto SEMPRE vindo da API (`active_limit`); nenhum numero de credencial e
  // literal nesta tela. `null` = V2 antiga sem eco de capacidade -> nao inventa.
  const slotsLeft = slotsRemaining(activeCount, activeLimit);
  const lastSlot = slotsLeft === 1;
  const promptCleanup = shouldPromptCleanup(activeCount, activeLimit);
  // Badge do topo e painel de faxina saem da MESMA moldura (L2) — ocupacao como
  // numero primario, pressao de vaga como qualificador derivado.
  const capacity = capacityFraming(activeCount, activeLimit);

  // Botao desabilitado tem de DIZER por que (M4). `disabled` nativo nao dispara
  // tooltip nem entra na ordem de foco: o motivo fica invisivel para teclado e
  // leitor de tela. Aqui o botao segue focavel com `aria-disabled`, o clique e
  // barrado no handler, e o motivo mora num texto VISIVEL apontado por
  // `aria-describedby`. `limitReached` implica `promptCleanup` (0 <= 2), entao o
  // painel de faxina — dono do id abaixo — esta sempre na tela quando isto vale.
  const blockedByCapacity = limitReached
    ? {
        "aria-disabled": true,
        "aria-describedby": CAPACITY_REASON_ID,
        className: "opacity-60 cursor-not-allowed",
      }
    : {};

  // Instante unico do render: todos os rotulos de "ha N dias" nesta tela saem
  // do MESMO relogio, senao duas linhas podem discordar na virada do dia.
  const now = Date.now();
  const candidates = revocationCandidates(tokens, now);

  const mismatch = kindMismatch(scopeState);
  const availableModes = SCOPE_MODES_BY_KIND[scopeState.kind] ?? SCOPE_MODES_BY_KIND.RK;
  // Erro de nome so aparece DEPOIS de o master digitar algo — nao acusa o campo
  // vazio antes de ele ter chance de preencher.
  const labelProblem = newLabel.trim() ? labelError(newLabel) : null;

  // Allowlist de IP — REATIVA a selecao de escopos (regra A espelhada em
  // `requiresIpAllowlist`, ../lib/tokenScopes). Marcar um escopo de escrita
  // torna o campo obrigatorio na hora, sem esperar o submit nem o 422 da V2.
  const ipEntries = parseIpAllowlist(ipAllowlistText);
  const ipRequired = requiresIpAllowlist(scopeState.scopes);
  const ipProblem = ipAllowlistError(ipEntries, scopeState.scopes);
  // O que a TELA mostra (erro inline + aria-invalid): só depois do toque — mas
  // uma vez que o campo TEM conteúdo, mostrar sempre (o usuário já digitou, um
  // formato inválido é acionável na hora). `ipProblem` cru segue gateando o
  // submit, tocado ou não.
  //
  // Máscara A: o veredito de CADA entrada sai por linha (`ipMarks`, marcadas
  // depois de o cursor sair da linha) — a razão de cada entrada inválida mora na
  // linha dela. O que sobra em `ipListProblemShown` é só o do CONJUNTO (vazio x
  // obrigatório, teto de entradas).
  const ipMarks = ipAllowlistEntryMarks(ipAllowlistText, ipCaret);
  const ipListProblem = ipAllowlistListError(ipEntries, scopeState.scopes);
  const ipListProblemShown = (ipTouched || ipEntries.length > 0) ? ipListProblem : null;
  const ipHasInvalidMark = ipMarks.some((mark) => mark.state === "invalid");
  // Key = texto da entrada + n.º da ocorrência (não o índice): colar no meio da lista não
  // remonta as linhas que não mudaram, e assim não re-anuncia os alertas delas.
  const ipMarkKeys = (() => {
    const seen = new Map();
    return ipMarks.map((mark) => {
      const n = seen.get(mark.entry) ?? 0;
      seen.set(mark.entry, n + 1);
      return `${mark.entry}#${n}`;
    });
  })();
  const ipDescribedBy = [
    "token-ip-allowlist-hint",
    ipListProblemShown && "token-ip-allowlist-error",
    ipMarks.length > 0 && "token-ip-allowlist-entries",
  ].filter(Boolean).join(" ");

  // Rotacao pendente reconstruida do DADO da lista a cada carga: o passo 3
  // (revogar a antiga) sobrevive a F5, logout e ao "já copiei".
  const rotationByOrigin = pendingRotationByOriginId(tokens, tokenKey);

  // Foco programatico no banner one-time (padrao da casa: NewOperation.jsx).
  useEffect(() => {
    if (createdToken?.secret && bannerRef.current) bannerRef.current.focus();
  }, [createdToken]);

  // Formulário mexeu ⇒ tentativa NOVA ⇒ chave de idempotência nova. Enquanto
  // o master não muda nada, os retries do step-up carregam a MESMA chave e a
  // V2 devolve a mesma criação em vez de emitir outra credencial.
  useEffect(() => {
    idempotencyKeyRef.current = newIdempotencyKey();
  }, [newLabel, scopeState, ipAllowlistText, rotatingFrom]);

  // Depois de uma colagem normalizada o React reescreve o campo e o navegador
  // manda o cursor para o fim; devolve-o ao fim da última entrada colada. Corre por
  // OPERAÇÃO de colagem (`ipPasteSeq`), não só por mudança de texto: a colagem cancela a
  // ação nativa, então com o texto igual a seleção anterior ficaria intacta.
  useLayoutEffect(() => {
    const pos = ipPendingCaretRef.current;
    if (pos !== null && ipInputRef.current) {
      ipInputRef.current.setSelectionRange(pos, pos);
      ipPendingCaretRef.current = null;
    }
  }, [ipAllowlistText, ipPasteSeq]);

  /** Payload válido, ou `null` (já avisando o master do que falta). */
  const montarPayload = () => {
    if (limitReached) {
      toast.error(`Limite de ${activeLimit} credenciais ativas atingido`);
      return null;
    }
    try {
      return buildCreateTokenPayload(scopeState, newLabel, ipEntries);
    } catch (err) {
      toast.error(err.message);
      return null;
    }
  };

  // Passo 1 do submit: valida e ABRE o step-up. Nada é criado aqui — o
  // segredo da credencial só existe depois da reprova de posse (FS-035).
  const createToken = () => {
    if (!montarPayload()) return;
    if (semSegundoFator) {   // defesa da UI; o backend recusa de qualquer forma
      setBlockOpen(true);
      return;
    }
    setStepUpError(null);
    setStepUpOpen(true);
  };

  /** Fecha o fluxo com o segredo na tela (caminho de sucesso, único ponto). */
  const concluirCriacao = (data) => {
    setCreatedToken({ ...data, rotated_from: rotatingFrom });
    setCopiedField(null);
    setCopyFailedField(null);
    setDismissArmed(false);
    setStepUpOpen(false);
    setShowNew(false);
    toast.success("Credencial criada");
    loadTokens();
  };

  // Passo 2: troca o código TOTP por uma prova single-use e REFAZ a criação
  // com ela no header. Os dois erros possíveis (prova e criação) ficam
  // INLINE no modal — fechar perderia o formulário que o master preencheu.
  const confirmStepUpAndCreate = async (code) => {
    const payload = montarPayload();
    if (!payload) return;

    setStepUpError(null);
    setSubmitting(true);
    let prova;
    try {
      const { data } = await api.post(STEP_UP_ENDPOINT, {
        code,
        action: STEP_UP_ACTION_TOKEN_CREATE,
      });
      prova = data?.step_up_token;
    } catch (e) {
      const status = e?.response?.status;
      if (isTwoFactorEnrollmentRequired(e)) {
        // O 2FA foi desativado em outra aba entre abrir a tela e confirmar.
        setStepUpOpen(false);
        setBlockOpen(true);
      } else {
        // 429 = rate-limit/lockout do backend (C5); o resto é código errado
        // ou expirado (C4). Nenhuma das duas mensagens diz QUAL é o caso do
        // servidor — dar detalhe aqui seria oráculo para quem só tem a sessão.
        setStepUpError(status === 429 ? COPY_STEPUP.rateLimited : COPY_STEPUP.invalidCode);
      }
      setSubmitting(false);
      return;
    }

    try {
      // TODO(F5): expor limites por credencial somente quando o contrato e o
      // enforcement por token estiverem fechados na V2.
      // TODO(F7): adicionar seletor somente com CRUD roteado ao banco do
      // ambiente escolhido; enviar apenas um prefixo "test" seria inseguro.
      //
      // `kind` vai SEMPRE no corpo (campo explicito, §2 do plano). Contra uma
      // V2 anterior à F3 o campo e IGNORADO (o modelo da V2 nao usa
      // extra="forbid"), nunca rejeitado — por isso a tela le a classe EMITIDA
      // da RESPOSTA, e nao da escolha do formulario.
      //
      // Headers EXPLÍCITOS nesta chamada, sem interceptor global: a prova vale
      // para UMA ação, e um interceptor a espalharia por requisições que não
      // pediram step-up.
      const { data } = await api.post("/v2/tokens", payload, {
        headers: {
          [STEP_UP_HEADER]: prova,
          "Idempotency-Key": idempotencyKeyRef.current,
        },
      });
      concluirCriacao(data);
    } catch (e) {
      if (isStepUpRequired(e)) {
        // Estado 3: prova expirada, consumida ou recusada. Reabre pedindo o
        // código ATUAL — nunca falha muda, nunca perde o formulário (C6).
        setStepUpError(COPY_STEPUP.proofExpired);
      } else if (isTwoFactorEnrollmentRequired(e)) {
        setStepUpOpen(false);
        setBlockOpen(true);
      } else {
        // 422 cru da V2 chega em ingles ("String should have at least 3
        // characters"); a tela e em portugues e o master nao le Pydantic.
        setStepUpOpen(false);
        toast.error(formatTokenCreateError(formatApiErrorDetail(e?.response?.data?.detail)));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Passo 1 da revogacao: ABRIR o dialogo nomeado. Nenhum caminho da tela
  // revoga sem passar por aqui (lista, faxina e rotacao usam o mesmo dialogo).
  const requestRevoke = (token, source = "list") => {
    if (!token) return;
    setRevokeTarget({ token, source });
    setRevokeTyped("");
  };

  // Passo 2: executar. So chega aqui depois da confirmacao explicita — e, para
  // credencial EM USO, depois de o master digitar o nome dela.
  const confirmRevoke = async () => {
    const token = revokeTarget?.token;
    if (!token) return;
    const tokenId = tokenKey(token);
    setRevoking(true);
    try {
      await api.delete(`/v2/tokens/${tokenId}`);
      toast.success(`Credencial “${token.label}” revogada`);
      if (createdToken?.rotated_from && tokenKey(createdToken.rotated_from) === tokenId) {
        setCreatedToken((prev) => (prev ? { ...prev, rotated_from: null } : prev));
      }
      setRevokeTarget(null);
      setRevokeTyped("");
      loadTokens();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRevoking(false);
    }
  };

  // A copia PODE falhar (permissão negada, contexto não-seguro, browser sem
  // Clipboard API). Falhar em silencio no unico momento em que o secret existe
  // e perder a credencial: o valor fica REVELADO e a tela ensina o caminho
  // manual. O ✓ verde so aparece em sucesso real.
  const copyToClipboard = async (value, field) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard indisponível");
      await navigator.clipboard.writeText(value);
      setCopyFailedField(null);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("[TokensAPI] clipboard falhou:", err?.message);
      setCopiedField(null);
      setCopyFailedField(field);
      if (field === "secret") setShowSecret(true); // nao esconder o que nao foi copiado
      toast.error(
        "Não consegui copiar automaticamente. Selecione o valor acima e copie manualmente (Ctrl+C) — ele não será exibido de novo."
      );
    }
  };

  const toggleScope = (scope) => setScopeState((prev) => toggleScopeState(scope, prev));

  // ---- Campo «IPs autorizados» — máscara A -------------------------------
  // Nenhuma tecla é bloqueada: o usuário escreve livremente e cada linha é
  // marcada ao vivo (depois de sair dela). Só o COLAR normaliza — os separadores
  // (quebra de linha, vírgula, ponto e vírgula, espaços) viram uma entrada por
  // linha, VISÍVEL no campo; nenhum outro caractere é removido.
  const onIpChange = (e) => {
    ipPasteCaretRef.current = null;
    setIpAllowlistText(e.target.value);
    setIpTouched(true);
    // Colar TEXTO SEM SEPARADOR não é interceptado (colar nativo: substitui a seleção em linha)
    // e se comporta como ESCREVER: o cursor fica na linha e a marca só vem quando ele sai dela
    // (folha, «depois de sair da linha»). Só a colagem NORMALIZADA (com separador) marca na hora
    // as entradas que colou (ver `onIpPaste`).
    setIpCaret(e.target.selectionStart);
  };
  const onIpCaretMove = (e) => {
    const pos = e.target.selectionStart;
    if (ipPasteCaretRef.current !== null) {
      if (pos === ipPasteCaretRef.current) return;
      ipPasteCaretRef.current = null;
    }
    setIpCaret(pos);
  };
  const onIpBlur = () => {
    ipPasteCaretRef.current = null;
    setIpTouched(true);
    setIpCaret(null);
  };
  const onIpPaste = (e) => {
    const field = e.target;
    const pasted = applyIpAllowlistPaste(
      field.value,
      field.selectionStart,
      field.selectionEnd,
      e.clipboardData?.getData("text")
    );
    if (!pasted) return; // nada a normalizar (só espaços): o navegador cola como sempre
    e.preventDefault();
    ipPendingCaretRef.current = pasted.caret;
    ipPasteCaretRef.current = pasted.caret;
    setIpPasteSeq((n) => n + 1);
    setIpAllowlistText(pasted.text);
    setIpTouched(true);
    // Colou: TODAS as entradas ganham marca, inclusive a última colada.
    setIpCaret(null);
  };

  const selectScopeMode = (mode) => setScopeState((prev) => nextScopeState(mode, prev));

  const selectKind = (kind) => setScopeState((prev) => nextKindState(kind, prev));

  // O dialogo nasce SEMPRE limpo: fechar no Cancelar/Esc nao pode deixar o
  // privilegio da sessao anterior pre-selecionado para o proximo token.
  const openCreateDialog = (open) => {
    if (open) {
      setNewLabel("");
      setScopeState(initialScopeState());
      setIpAllowlistText("");
      setIpTouched(false);
      setIpCaret(null);
      setRotatingFrom(null);
      setDroppedScopes([]);
    }
    setShowNew(open);
  };

  // Rotacao: NAO existe endpoint /rotate (removido de proposito pela V2). Este
  // atalho so pre-preenche a criacao com label e escopos da credencial atual;
  // revogar a antiga segue sendo um ato separado, DEPOIS de migrar.
  const openRotateDialog = (token) => {
    const prefill = rotationPrefill(token);
    setNewLabel(prefill.label);
    setScopeState(prefill.state);
    // A allowlist da origem entra pre-preenchida SO quando a API a devolve na
    // lista. Sem isto uma rotacao de credencial com escrita nasceria bloqueada
    // e o master teria de redigitar os IPs de cabeca — o passo "migrar" viraria
    // um formulario novo. Quando a V2 nao devolve o campo, cai em vazio e o
    // campo obrigatorio se explica sozinho (nada e inventado).
    setIpAllowlistText(
      (Array.isArray(token?.ip_allowlist) ? token.ip_allowlist : []).join("\n")
    );
    setIpTouched(false);
    setIpCaret(null);
    setRotatingFrom(token);
    setDroppedScopes(prefill.droppedScopes);
    setShowNew(true);
  };

  const statusColor = {
    active: "bg-emerald-100 text-emerald-800 border-emerald-200",
    revoked: "bg-zinc-100 text-zinc-600 border-zinc-200",
    expired: "bg-amber-100 text-amber-800 border-amber-200",
  };

  const statusLabel = {
    active: "Ativo",
    revoked: "Revogado",
    expired: "Expirado",
  };

  const issuedKind = issuedCredentialKind(createdToken);
  const issuedMeta = issuedKind ? CREDENTIAL_KIND_META[issuedKind] : null;

  return (
    <div className="space-y-6" data-testid="tokens-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tokens API</h1>
          <p className="text-sm text-zinc-500 mt-1">Crie e gerencie credenciais para integrar com a API V2 da Zemo</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {syncStatus === "offline" && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-700" data-testid="sync-offline-badge">
              <CloudOff className="h-3 w-3" />
              <span>V2 indisponível</span>
            </div>
          )}
          {syncStatus === "synced" && tokens.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs text-emerald-700" data-testid="sync-ok-badge">
              <Check className="h-3 w-3" />
              <span>Sincronizado</span>
            </div>
          )}
          {capacity && (
            <div
              className={`px-2.5 py-1 rounded-full border text-xs ${
                limitReached || lastSlot
                  ? "bg-amber-50 border-amber-200 text-amber-700"
                  : "bg-zinc-50 border-zinc-200 text-zinc-600"
              }`}
              data-testid="token-capacity-badge"
            >
              {capacity.primary}
              {(limitReached || lastSlot) && (
                <span className="font-semibold"> · {capacity.qualifier}</span>
              )}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={loadTokens} disabled={loading} data-testid="token-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button
            onClick={() => {
              if (limitReached) return;
              // Sem 2FA o botão NÃO some (botão sumido parece bug): ele abre o
              // modal que explica o corte e ensina o caminho (FS-035, C2).
              if (semSegundoFator) { setBlockOpen(true); return; }
              openCreateDialog(true);
            }}
            title={limitReached
              ? "Sem vagas — revogue uma credencial ativa antes de criar outra"
              : undefined}
            {...blockedByCapacity}
            data-testid="token-new-btn"
          >
            <Plus className="h-4 w-4 mr-2" aria-hidden="true" /> Criar credencial
          </Button>
        </div>
      </div>

      {/* ------------------------------------------------------------------
          FS-035 — corte IMEDIATO de quem não tem 2FA (decisão do owner Q2=c).

          Banner PERSISTENTE (não-dispensável) e no topo: o master precisa
          descobrir o corte ANTES de preencher o formulário inteiro, não
          depois de um 403. A remediação é self-service e leva ~2min, por isso
          o CTA leva direto ao perfil e traz o retorno embutido.
          ------------------------------------------------------------------ */}
      {semSegundoFator && (
        <InlineAlert
          tone="warning"
          icon={ShieldAlert}
          testId="token-2fa-required-banner"
        >
          {COPY_STEPUP.bannerBody}
          <a
            href={PERFIL_2FA_LINK}
            className="ml-2 inline-block font-semibold underline underline-offset-2"
            data-testid="token-2fa-banner-cta"
          >
            {COPY_STEPUP.bannerCta}
          </a>
        </InlineAlert>
      )}

      {/* One-time Secret Display */}
      {createdToken && createdToken.secret && (
        <div
          className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 focus:outline-none focus:ring-2 focus:ring-amber-400"
          data-testid="token-secret-banner"
          ref={bannerRef}
          tabIndex={-1}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1 space-y-3">
              <div>
                <p className="font-semibold text-amber-800">Credencial criada — valores visíveis UMA ÚNICA VEZ</p>
                <p className="text-xs text-amber-700 mt-1">
                  Copie e armazene em local seguro. Estes valores não serão exibidos novamente.
                </p>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-xs font-medium text-zinc-600 mb-1">Client ID</p>
                  <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2">
                    <code className="flex-1 text-sm font-mono text-zinc-800 break-all" data-testid="token-client-id-value">
                      {createdToken.client_id}
                    </code>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => copyToClipboard(createdToken.client_id, "client_id")}
                      data-testid="token-client-id-copy-btn"
                      aria-label="Copiar client_id"
                    >
                      {copiedField === "client_id" ? (
                        <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                      ) : (
                        <Copy className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                  {copyFailedField === "client_id" && (
                    <p className="text-xs text-red-700 mt-1" role="alert" data-testid="token-client-id-copy-failed">
                      Não consegui copiar automaticamente. Selecione o valor acima e copie
                      manualmente (Ctrl+C) — ele não será exibido de novo.
                    </p>
                  )}
                </div>

                <div>
                  <p className="text-xs font-medium text-zinc-600 mb-1">Secret</p>
                  <div className="flex items-center gap-2 bg-white border border-amber-200 rounded-md px-3 py-2">
                    <code className="flex-1 text-sm font-mono text-zinc-800 break-all" data-testid="token-secret-value">
                      {showSecret ? createdToken.secret : createdToken.secret.slice(0, 8) + "..." + createdToken.secret.slice(-4)}
                    </code>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setShowSecret(!showSecret)}
                      data-testid="token-secret-toggle-btn"
                      aria-label={showSecret ? "Ocultar secret" : "Exibir secret"}
                    >
                      {showSecret ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => copyToClipboard(createdToken.secret, "secret")}
                      data-testid="token-secret-copy-btn"
                      aria-label="Copiar secret"
                    >
                      {copiedField === "secret" ? (
                        <Check className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                      ) : (
                        <Copy className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Button>
                  </div>
                  {copyFailedField === "secret" && (
                    <p className="text-xs text-red-700 mt-1" role="alert" data-testid="token-secret-copy-failed">
                      Não consegui copiar automaticamente. Selecione o valor acima e copie
                      manualmente (Ctrl+C) — ele não será exibido de novo.
                    </p>
                  )}
                </div>
              </div>

              {/* O prefixo do secret e o sinal da classe. Ele e LIDO da resposta
                  (nunca da escolha do formulario): assim, contra uma V2 anterior
                  à F3 — que ignora `kind` e sempre emite `sk_live_` — a tela diz
                  a verdade sobre o que foi realmente emitido. */}
              {issuedMeta && (
                <div className="rounded-md bg-white border border-amber-200 px-3 py-2" data-testid="token-kind-explainer">
                  <p className="text-xs text-zinc-700">
                    <code className="font-mono font-semibold">{issuedMeta.prefix}</code>{" "}
                    — {issuedMeta.secretHint}
                  </p>
                </div>
              )}

              {/* "E agora, como uso?" (M8). O banner explicava a CLASSE da
                  credencial e parava ali — quem chegou aqui pela primeira vez
                  ficava com dois valores na mao e nenhum proximo passo. O
                  caminho recomendado e o token exchange (o header
                  X-Client-Id/X-Client-Secret esta deprecated na doc), entao e
                  ELE que a tela ensina. */}
              <div className="rounded-md bg-white border border-amber-200 px-3 py-2" data-testid="token-next-step">
                <p className="text-xs text-zinc-700">
                  <strong>Próximo passo:</strong> troque estes valores por um token de
                  acesso — <code className="font-mono">POST /v1/auth/token</code> com{" "}
                  <code className="font-mono">client_id</code> e{" "}
                  <code className="font-mono">client_secret</code> no corpo — e envie o{" "}
                  <code className="font-mono">access_token</code> devolvido no header{" "}
                  <code className="font-mono">Authorization: Bearer</code>.
                </p>
                <a
                  href={AUTH_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-medium text-[var(--zemo-primary)] underline mt-1 inline-block"
                  data-testid="token-docs-link"
                >
                  Ver a documentação de autenticação (abre em nova aba)
                </a>
              </div>

              {createdToken.rotated_from && (
                <div className="rounded-md bg-white border border-amber-300 px-3 py-2 space-y-2" data-testid="token-rotation-reminder">
                  <p className="text-xs text-amber-800">
                    <strong>Rotação em andamento.</strong> Migre a integração para esta credencial
                    e só então revogue a anterior (“{createdToken.rotated_from.label}”). As duas
                    ficam ativas e <strong>ocupam vaga</strong> enquanto a antiga não for revogada.
                  </p>
                  <p className="text-xs text-amber-700">
                    Se sair desta tela agora, o lembrete <strong>não se perde</strong>: a
                    credencial anterior continua marcada como “rotação pendente” na lista abaixo.
                  </p>
                  <Button
                    variant="outline" size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => requestRevoke(createdToken.rotated_from, "rotation")}
                    data-testid="token-rotation-revoke-old-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Já migrei — revogar a anterior
                  </Button>
                </div>
              )}

              {/* Confirmacao secundaria: fechar o banner com uma rotacao ainda
                  aberta e o momento em que o passo 3 costuma sumir. O lembrete
                  passa a viver na lista (derivado do dado), e o botao diz isso. */}
              {dismissArmed ? (
                <div className="space-y-2" data-testid="token-secret-dismiss-confirm">
                  <p className="text-xs text-amber-800">
                    O secret <strong>não</strong> será exibido de novo. Guardou em local seguro?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setCreatedToken(null); setShowSecret(false); setDismissArmed(false); }}
                      data-testid="token-secret-dismiss-confirm-btn"
                    >
                      Sim, guardei — fechar
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setDismissArmed(false)}
                      data-testid="token-secret-dismiss-cancel-btn"
                    >
                      Voltar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline" size="sm"
                  onClick={() => {
                    // Com rotacao aberta, confirma duas vezes; sem ela, fecha direto.
                    if (createdToken.rotated_from) setDismissArmed(true);
                    else { setCreatedToken(null); setShowSecret(false); }
                  }}
                  data-testid="token-secret-dismiss-btn"
                >
                  Já copiei e guardei o secret
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Faxina de vagas — R9 opcao 1 (§7.2): com validade indefinida nada
          expira sozinho, entao a vaga so volta por revogacao do master. */}
      {/* Semantica de COR por estado (M7). O painel era azul (informativo) com
          um AlertTriangle (aviso) dentro: a cor dizia uma coisa e o icone
          dizia outra. Agora sem vagas — que BLOQUEIA criar e rotacionar — e
          aviso (âmbar + AlertTriangle); pressao de vaga ainda com folga e
          informativo (azul + Info). Nenhum dos dois e destrutivo: o vermelho
          fica so nos botoes de revogar. */}
      {promptCleanup && (
        <div
          className={`rounded-lg border p-4 ${
            limitReached ? ALERT_TONE.warning : ALERT_TONE.info
          }`}
          data-testid="token-cleanup-panel"
        >
          <div className="flex items-start gap-3">
            {limitReached ? (
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
            ) : (
              <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" aria-hidden="true" />
            )}
            <div className="flex-1 space-y-3">
              <div>
                {/* Este texto e o motivo apontado por `aria-describedby` dos
                    botoes criar/rotacionar quando eles estao bloqueados (M4). */}
                <p
                  id={CAPACITY_REASON_ID}
                  className={`text-sm font-semibold ${limitReached ? "text-amber-900" : "text-blue-900"}`}
                  data-testid="token-cleanup-headline"
                >
                  {capacity && `${capacity.primary} · ${capacity.qualifier}`}
                  {limitReached && " — revogue uma credencial ativa para criar ou rotacionar outra"}
                </p>
                <p className={`text-xs mt-1 ${limitReached ? "text-amber-800" : "text-blue-800"}`}>
                  {candidates.length > 0 ? (
                    <>
                      Credenciais não expiram sozinhas: a vaga só volta quando você revoga.
                      Comece pelas que não estão em uso.
                    </>
                  ) : (
                    <>
                      Todas as credenciais ativas foram usadas recentemente — nenhuma parece
                      ociosa. Revise a lista antes de criar outra.
                    </>
                  )}
                </p>
              </div>
              {/* Só credenciais OCIOSAS ganham botão de revogar aqui. Sem
                  nenhuma ociosa, o painel é informativo e NÃO oferece a ação:
                  pressão de vaga não pode empurrar a revogação de uma
                  credencial em uso. A lista completa segue abaixo. */}
              {candidates.length > 0 && (
                <div className="space-y-2" data-testid="token-cleanup-candidates">
                  {candidates.slice(0, 3).map(({ token, usage }) => (
                    <div
                      key={tokenKey(token)}
                      className={`flex items-center justify-between gap-3 bg-white border rounded-md px-3 py-2 ${
                        limitReached ? "border-amber-200" : "border-blue-200"
                      }`}
                      data-testid={`token-cleanup-candidate-${tokenKey(token)}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-zinc-800 truncate">{token.label}</p>
                        {/* Neutro de proposito: quem carrega a semantica de cor
                            e o PAINEL. Ambar aqui dentro de um painel azul era
                            um terceiro tom competindo com os outros dois. */}
                        <p className="text-xs text-zinc-600">
                          {usage.label}
                          {token.created_at && <> · criada em {formatDateTimeBR(token.created_at)}</>}
                        </p>
                      </div>
                      <Button
                        variant="outline" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 shrink-0"
                        onClick={() => requestRevoke(token, "cleanup")}
                        data-testid={`token-cleanup-revoke-${tokenKey(token)}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Revogar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Token List */}
      {tokens.length === 0 && !loading ? (
        <div className="bg-white border border-[var(--zemo-border)] rounded-lg p-12 text-center" data-testid="tokens-empty">
          <Key className="h-10 w-10 text-zinc-300 mx-auto mb-3" aria-hidden="true" />
          <p className="text-zinc-500">Nenhuma credencial criada</p>
          <p className="text-xs text-zinc-500 mt-1">
            Crie uma credencial para integrar seu ERP diretamente com a API V2
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="tokens-list">
          {tokens.map((token) => {
            const usage = lastUsedSummary(token, now);
            const kind = issuedCredentialKind(token);
            const meta = kind ? CREDENTIAL_KIND_META[kind] : null;
            // O prefixo só é exibido quando a V2 AFIRMOU o `kind` (pós-F3). Sem
            // esse campo a classe é DERIVADA dos escopos, e derivar o prefixo
            // junto seria mentira: uma credencial legada, emitida com secret
            // `sk_live_` e menos de 9 escopos, aparecia como "RK · rk_live_".
            // Auto-cura assim que o GET passar a devolver `kind`.
            const kindIsAuthoritative = CREDENTIAL_KINDS.includes(token?.kind);
            const rotationSuccessor = rotationByOrigin.get(tokenKey(token));
            return (
            <div
              key={tokenKey(token)}
              className="bg-white border border-[var(--zemo-border)] rounded-lg p-4"
              data-testid={`token-item-${tokenKey(token)}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Key className="h-4 w-4 text-zinc-400" />
                  <div>
                    <p className="text-sm font-semibold text-zinc-800">{token.label}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${statusColor[token.status] || "bg-zinc-100 text-zinc-600"}`}>
                        {statusLabel[token.status] || token.status}
                      </span>
                      {meta && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full border bg-zinc-50 border-zinc-200 text-zinc-700"
                          title={
                            kindIsAuthoritative
                              ? meta.summary
                              : `${meta.summary} (classe deduzida das permissões — esta versão da API ainda não informa o tipo da credencial)`
                          }
                          data-testid={`token-kind-badge-${tokenKey(token)}`}
                        >
                          {kindIsAuthoritative ? `${meta.short} · ${meta.prefix}` : meta.short}
                        </span>
                      )}
                      {rotationSuccessor && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full border bg-amber-50 border-amber-200 text-amber-800"
                          title={`Substituída por “${rotationSuccessor.label}”. Depois de migrar a integração, revogue esta.`}
                          data-testid={`token-rotation-pending-${tokenKey(token)}`}
                        >
                          rotação pendente — revogar após migrar
                        </span>
                      )}
                      {/* L4: texto informativo pequeno em zinc-500 (>= AA);
                          zinc-400 dava ~2.3:1 e reprovava para 12px. */}
                      {token.client_id && (
                        <span className="text-xs text-zinc-500 font-mono">
                          {token.client_id}
                        </span>
                      )}
                      {token.scopes && (
                        <span className="text-xs text-zinc-500">
                          {(Array.isArray(token.scopes) ? token.scopes : []).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {token.status === "active" && (
                    <>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => { if (!limitReached) openRotateDialog(token); }}
                        title={limitReached
                          ? "Sem vaga — revogue uma credencial ativa antes de rotacionar"
                          : "Criar uma nova credencial com os mesmos escopos"}
                        {...blockedByCapacity}
                        data-testid={`token-rotate-${tokenKey(token)}`}
                      >
                        <RotateCw className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> Rotacionar
                      </Button>
                      <Button
                        variant="outline" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => requestRevoke(token, rotationSuccessor ? "rotation" : "list")}
                        data-testid={`token-revoke-${tokenKey(token)}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {rotationSuccessor ? "Já migrei — revogar" : "Revogar"}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {(token.status === "active" || token.created_at || token.revoked_at) && (
              <div className="mt-2 text-xs text-zinc-500 flex flex-wrap gap-x-1">
                {token.status === "active" && (
                  <span
                    className={usage.stale ? "text-amber-700" : "text-zinc-500"}
                    data-testid={`token-last-used-${tokenKey(token)}`}
                  >
                    {usage.label}
                  </span>
                )}
                {token.created_at && (
                  <>
                    {token.status === "active" && <span>·</span>}
                    <span>Criado em {formatDateTimeBR(token.created_at)}</span>
                  </>
                )}
                {token.revoked_at && <span>· Revogado em {formatDateTimeBR(token.revoked_at)}</span>}
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Create Token Dialog */}
      <Dialog open={showNew} onOpenChange={openCreateDialog}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="token-new-dialog">
          <DialogHeader>
            <DialogTitle>{rotatingFrom ? "Rotacionar credencial" : "Criar credencial"}</DialogTitle>
            <DialogDescription>
              {rotatingFrom
                ? "Uma NOVA credencial será criada com os mesmos escopos. Migre a integração e revogue a anterior depois — as duas ocupam vaga enquanto ativas."
                : "Crie uma credencial para integrar seu ERP diretamente com a API V2 da Zemo. Ela será ativada imediatamente e, nesta fase, será exclusiva de Produção."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="token-label">Nome da credencial</Label>
              <Input
                id="token-label"
                placeholder="Ex: Integração ERP"
                value={newLabel}
                maxLength={LABEL_MAX}
                aria-invalid={Boolean(labelProblem)}
                aria-describedby={labelProblem ? "token-label-error" : undefined}
                onChange={(e) => setNewLabel(e.target.value)}
                data-testid="token-label-input"
              />
              {labelProblem && (
                <p
                  id="token-label-error"
                  className="text-xs text-red-700 mt-1"
                  role="alert"
                  data-testid="token-label-error"
                >
                  {labelProblem}
                </p>
              )}
            </div>
            {/* M7: os tres avisos eram <p> ambar soltos, fora do padrao de
                alerta da casa. Agora saem pelo mesmo componente de caixa. */}
            {limitReached && (
              <InlineAlert icon={AlertTriangle} testId="token-capacity-warning">
                Revogue uma credencial ativa antes de criar outra.
              </InlineAlert>
            )}
            {lastSlot && !limitReached && (
              <InlineAlert icon={AlertTriangle} testId="token-last-slot-warning">
                Esta é a <strong>última vaga</strong> de {activeLimit}. Credenciais não expiram
                sozinhas — depois desta, só revogando uma ativa para criar outra.
              </InlineAlert>
            )}
            {droppedScopes.length > 0 && (
              <InlineAlert icon={AlertTriangle} testId="token-rotation-dropped-scopes">
                A credencial de origem tem escopos fora do catálogo self-service
                ({droppedScopes.join(", ")}). Eles <strong>não</strong> serão replicados.
              </InlineAlert>
            )}

            {/* Passo 1 — classe da credencial. Escolha EXPLICITA (o campo `kind`
                vai no corpo do POST); RK e o default recomendado.
                L6: a numeracao dos passos vivia SO neste comentario — quem usa a
                tela nao le comentario. Agora ela esta na legend. */}
            <fieldset>
              <legend className="text-sm font-medium text-zinc-900">1. Tipo de credencial</legend>
              <div className="space-y-2 mt-2" data-testid="token-kind-options">
                {CREDENTIAL_KINDS.map((kind) => {
                  const meta = CREDENTIAL_KIND_META[kind];
                  return (
                    <label
                      key={kind}
                      className={`flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer ${
                        scopeState.kind === kind ? "border-[var(--zemo-primary)] bg-blue-50" : "border-zinc-200"
                      }`}
                      data-testid={`token-kind-${kind}`}
                    >
                      <input
                        type="radio"
                        name="token-kind"
                        value={kind}
                        checked={scopeState.kind === kind}
                        onChange={() => selectKind(kind)}
                        className="mt-1 border-zinc-300"
                      />
                      <span>
                        <span className="block font-medium text-zinc-900">
                          {meta.title}
                          {kind === "RK" && (
                            <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                              recomendado
                            </span>
                          )}
                        </span>
                        <span className="block text-xs text-zinc-600">{meta.summary}</span>
                        <code className="text-xs text-zinc-500">
                          o secret começa com {meta.prefix}
                        </code>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Passo 2 — escopos. SK e o recorte FIXO dos 9 por definicao, entao
                nao ha o que escolher; so RK abre os presets (e o catalogo de 12). */}
            {scopeState.kind === "SK" ? (
              // O passo 2 NAO some na SK — so nao tem o que escolher. Sem a
              // legend numerada aqui, a numeracao pularia de "1." para o rodape.
              <fieldset>
                <legend className="text-sm font-medium text-zinc-900">
                  2. Permissões da credencial
                </legend>
                <p
                  className="flex items-start gap-2 text-sm text-zinc-700 rounded-lg border border-zinc-200 p-3 mt-2"
                  data-testid="token-scope-full-notice"
                >
                  <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-zinc-400" aria-hidden="true" />
                  <span>
                    Acesso completo inclui as {SK_SCOPE_COUNT} permissões dele — não as{" "}
                    {SCOPE_COUNT} do catálogo. Para restringir, ou para usar as demais
                    permissões, escolha <strong>escopo mínimo (RK)</strong> acima.
                  </span>
                </p>
              </fieldset>
            ) : (
              <fieldset>
                <legend className="text-sm font-medium text-zinc-900">
                  2. Permissões da credencial
                </legend>
                <div className="space-y-2 mt-2" data-testid="token-scope-modes">
                  {[
                    ["read", "Visualização", `Somente consulta: ${listPtBr(TOKEN_SCOPE_READ_PRODUCTS)}`],
                    ["custom", "Configuração personalizada", `Escolha entre as ${SCOPE_COUNT} permissões`],
                  ]
                    .filter(([mode]) => availableModes.includes(mode))
                    .map(([mode, title, hint]) => (
                      <label
                        key={mode}
                        className={`flex items-start gap-2 rounded-lg border p-3 text-sm cursor-pointer ${
                          scopeState.mode === mode ? "border-[var(--zemo-primary)] bg-blue-50" : "border-zinc-200"
                        }`}
                        data-testid={`token-scope-mode-${mode}`}
                      >
                        <input
                          type="radio"
                          name="token-scope-mode"
                          value={mode}
                          checked={scopeState.mode === mode}
                          onChange={() => selectScopeMode(mode)}
                          className="mt-1 border-zinc-300"
                          {...(mode === "custom"
                            ? {
                                "aria-expanded": scopeState.mode === "custom",
                                "aria-controls": "token-scope-custom-panel",
                              }
                            : {})}
                        />
                        <span>
                          <span className="block font-medium text-zinc-900">{title}</span>
                          <span className="block text-xs text-zinc-600">{hint}</span>
                        </span>
                      </label>
                    ))}
                </div>
              </fieldset>
            )}
            {scopeState.mode === "custom" && (
              <div className="space-y-4" id="token-scope-custom-panel" data-testid="token-scope-custom">
                {TOKEN_SCOPE_GROUPS.map((group) => (
                  <fieldset key={group.id}>
                    <legend className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      {group.label}
                    </legend>
                    <div className="space-y-2 mt-2">
                      {group.options.map((scope) => (
                        <label
                          key={scope.value}
                          className="flex items-start gap-2 text-sm cursor-pointer"
                          data-testid={`token-scope-${scope.value}`}
                        >
                          <input
                            type="checkbox"
                            checked={scopeState.scopes.includes(scope.value)}
                            onChange={() => toggleScope(scope.value)}
                            className="mt-1 rounded border-zinc-300"
                          />
                          <span>
                            <span className="block text-zinc-800">
                              <strong>{scope.product}</strong>: {scope.description}
                            </span>
                            <code className="text-xs text-zinc-500">{scope.value}</code>
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}
            {/* Coerencia classe x escopos: a V2 responde 422 nas duas colisoes
                (SK sem o recorte dos 9 EXATO, RK CONTENDO os 9). A tela para antes. */}
            {mismatch && (
              <InlineAlert
                icon={AlertTriangle}
                role="alert"
                testId="token-kind-mismatch"
              >
                {mismatch}
              </InlineAlert>
            )}
            {/* Passo 3 — allowlist de IP (FS-032, fase B create-time).
                A obrigatoriedade e CONDICIONAL e reativa: `requiresIpAllowlist`
                espelha a regra A da V2 (nao-`:read`, exceto `simulation:create`).
                A validacao DURA e da V2 (422 nomeado); isto aqui e UX — bloqueia
                o submit e explica antes da viagem, nunca a unica defesa. */}
            <fieldset>
              <legend className="text-sm font-medium text-zinc-900">
                {COPY.ipAllowlistLegend}
              </legend>
              <div className="mt-2 space-y-2">
                <Label htmlFor="token-ip-allowlist" className="flex flex-wrap items-center gap-2">
                  <span>{COPY.ipAllowlistLabel}</span>
                  {ipRequired ? (
                    <span
                      className="text-xs font-normal px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                      data-testid="token-ip-required-tag"
                    >
                      {COPY.ipAllowlistRequiredTag}
                    </span>
                  ) : (
                    <span className="text-xs font-normal text-zinc-500">
                      {COPY.ipAllowlistOptional}
                    </span>
                  )}
                </Label>
                <Textarea
                  id="token-ip-allowlist"
                  rows={3}
                  className="font-mono text-sm"
                  placeholder={COPY.ipAllowlistPlaceholder}
                  value={ipAllowlistText}
                  aria-required={ipRequired}
                  ref={ipInputRef}
                  aria-invalid={Boolean(ipListProblemShown) || ipHasInvalidMark}
                  aria-describedby={ipDescribedBy}
                  onChange={onIpChange}
                  onSelect={onIpCaretMove}
                  onFocus={onIpCaretMove}
                  onPaste={onIpPaste}
                  onBlur={onIpBlur}
                  data-testid="token-ip-allowlist-input"
                />
                <p id="token-ip-allowlist-hint" className="text-xs text-zinc-600">
                  {COPY.ipAllowlistHint}
                  {/* Dica de contexto: o NAT de producao. PLACEHOLDER/hint —
                      jamais pre-preenchido no campo. */}
                  <span className="block mt-1">{COPY.ipAllowlistNatHint}</span>
                  {ipEntries.length > 0 && (
                    <span className="block mt-1 text-zinc-500" data-testid="token-ip-allowlist-count">
                      {COPY.ipAllowlistCount(ipEntries.length)}
                    </span>
                  )}
                </p>
                {/* Por que a lista e exigida (escrita) ou opcional (sem escrita) —
                    o motivo aparece JUNTO do campo, nao so no erro. UM dos dois,
                    conforme `ipRequired` (TK2-A C1004423). */}
                {ipRequired ? (
                  <p className="text-xs text-zinc-600" data-testid="token-ip-required-why">
                    {COPY.ipAllowlistRequiredWhy}
                  </p>
                ) : (
                  <p className="text-xs text-zinc-600" data-testid="token-ip-readonly-why">
                    {COPY.ipAllowlistReadOnlyWhy}
                  </p>
                )}
                {/* UX-4: erro so DEPOIS do toque — o campo pristino nao grita
                    "obrigatorio" so por marcar um scope de escrita. O bloqueio
                    de submit e independente (ver `disabled` do botao). */}
                {ipListProblemShown && (
                  <p
                    id="token-ip-allowlist-error"
                    className="text-xs text-red-700"
                    role="alert"
                    data-testid="token-ip-allowlist-error"
                  >
                    {ipListProblemShown}
                  </p>
                )}
                {/* Máscara A: cada entrada do campo, marcada válida/inválida
                    DEPOIS de o cursor sair da linha. Marca = ícone + cor; a
                    razão da inválida é a mensagem de sempre (`COPY_IP`), na
                    linha dela. A linha sob o cursor fica neutra (em escrita). */}
                {ipMarks.length > 0 && (
                  <ul
                    id="token-ip-allowlist-entries"
                    className="space-y-1 text-xs"
                    data-testid="token-ip-allowlist-entries"
                  >
                    {ipMarks.map((mark, index) => (
                      <li
                        key={ipMarkKeys[index]}
                        className="flex items-start gap-1.5"
                        data-testid={`token-ip-allowlist-entry-${index}`}
                        data-state={mark.state}
                      >
                        {mark.state === "valid" && (
                          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                        )}
                        {mark.state === "invalid" && (
                          <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-700" aria-hidden="true" />
                        )}
                        {mark.state === "editing" && (
                          <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-zinc-400" aria-hidden="true" />
                        )}
                        {mark.state === "invalid" ? (
                          <span
                            role="alert"
                            className="text-red-700"
                            data-testid={`token-ip-allowlist-entry-error-${index}`}
                          >
                            {mark.error}
                          </span>
                        ) : (
                          <code className="font-mono text-zinc-700 break-all">{mark.entry}</code>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </fieldset>
            {/* L1: o prefixo do secret era explicado TRES vezes no mesmo fluxo —
                na opcao do radio, neste rodape e no banner pos-criacao. O rodape
                era o repeteco puro: a opcao escolhida esta visivel logo acima,
                dizendo o mesmo. Ficaram os dois que informam algo novo: a opcao
                (para COMPARAR antes de escolher) e o banner (para RECONHECER o
                que a V2 de fato emitiu). */}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => openCreateDialog(false)} data-testid="token-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={createToken}
              disabled={
                submitting ||
                limitReached ||
                Boolean(mismatch) ||
                Boolean(labelProblem) ||
                Boolean(ipProblem)
              }
              data-testid="token-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              {rotatingFrom ? "Criar nova credencial" : "Criar credencial"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------
          FS-035 — modal de BLOQUEIO (C2), para quem ainda não tem 2FA.

          Aberto pelo clique em "Criar credencial" e, como rede de segurança,
          pelo 403 `two_factor_enrollment_required` (o 2FA pode ter sido
          desativado em outra aba no meio do caminho). A copy é franca sobre
          reprencher o formulário: prometer que a tela guardaria o rascunho
          seria mentira — o retorno do perfil recarrega a aplicação.
          ------------------------------------------------------------------ */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent className="max-w-md" data-testid="token-2fa-block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" aria-hidden="true" />
              {COPY_STEPUP.blockTitle}
            </DialogTitle>
            <DialogDescription>{COPY_STEPUP.blockBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBlockOpen(false)}
              data-testid="token-2fa-block-dismiss"
            >
              {COPY_STEPUP.blockSecondary}
            </Button>
            <Button asChild data-testid="token-2fa-block-cta">
              <a href={PERFIL_2FA_LINK}>{COPY_STEPUP.blockCta}</a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FS-035 — step-up (C3/C4/C5/C6). Sobe POR CIMA do diálogo de criação,
          que fica aberto: o formulário preenchido não pode se perder entre a
          confirmação e um eventual erro de código. */}
      <StepUpDialog
        open={stepUpOpen}
        onOpenChange={(aberto) => {
          setStepUpOpen(aberto);
          if (!aberto) setStepUpError(null);
        }}
        onSubmit={confirmStepUpAndCreate}
        submitting={submitting}
        error={stepUpError}
      />

      {/* ------------------------------------------------------------------
          Revogação — ação IRREVERSÍVEL e IMEDIATA.

          Um `window.confirm` genérico ("Revogar este token?") não diz QUAL
          credencial, nem o que acontece, e ainda por cima é um diálogo do
          browser fora do padrão visual da casa. Este AlertDialog: (a) NOMEIA a
          credencial (label + client_id), (b) declara a consequência em tempo
          presente, (c) mostra a última autenticação, e (d) para credencial que
          NÃO está ociosa, exige que o master digite o nome dela. É o mesmo
          diálogo para os três gatilhos — lista, faxina e rotação.
          ------------------------------------------------------------------ */}
      <AlertDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => { if (!open && !revoking) { setRevokeTarget(null); setRevokeTyped(""); } }}
      >
        <AlertDialogContent data-testid="token-revoke-dialog">
          {revokeTarget && (() => {
            const token = revokeTarget.token;
            const usage = lastUsedSummary(token, now);
            // "Em uso" = não ociosa. Aí a confirmação por digitação entra.
            const inUse = !usage.stale && !usage.neverUsed;
            const typedOk = !inUse || revokeTyped.trim() === (token.label ?? "").trim();
            const successor = rotationByOrigin.get(tokenKey(token));
            return (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revogar “{token.label}”?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-2 text-left">
                      <p>
                        Toda integração que usa esta credencial passa a receber{" "}
                        <strong>401 agora</strong>. O secret <strong>não pode ser recuperado</strong>{" "}
                        — para voltar a integrar é preciso criar outra credencial e migrar.
                      </p>
                      {token.client_id && (
                        <p className="text-xs">
                          Credencial:{" "}
                          <code className="font-mono" data-testid="token-revoke-client-id">
                            {String(token.client_id).slice(0, 14)}…
                          </code>
                        </p>
                      )}
                      <p className="text-xs" data-testid="token-revoke-last-used">
                        Última autenticação: <strong>{usage.label}</strong>
                        {token.created_at && <> · criada em {formatDateTimeBR(token.created_at)}</>}
                      </p>
                      {successor && (
                        <p className="text-xs text-amber-800" data-testid="token-revoke-rotation-note">
                          Esta credencial foi substituída por “{successor.label}”. Revogue só
                          depois de confirmar que a integração já usa a nova.
                        </p>
                      )}
                      {inUse && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-2">
                          <p className="text-xs text-amber-900" data-testid="token-revoke-in-use-warning">
                            Esta credencial <strong>está em uso</strong> ({usage.label}). Para
                            confirmar, digite o nome dela abaixo.
                          </p>
                          <Input
                            value={revokeTyped}
                            onChange={(e) => setRevokeTyped(e.target.value)}
                            placeholder={token.label}
                            aria-label={`Digite ${token.label} para confirmar a revogação`}
                            data-testid="token-revoke-typed-input"
                          />
                        </div>
                      )}
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={revoking} data-testid="token-revoke-cancel-btn">
                    Cancelar
                  </AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                    disabled={revoking || !typedOk}
                    onClick={(e) => { e.preventDefault(); confirmRevoke(); }}
                    data-testid="token-revoke-confirm-btn"
                  >
                    {revoking && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Revogar definitivamente
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            );
          })()}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
